import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { User, Mail, Phone, Save, Loader2, Shield, Building2, Lock, Eye, EyeOff, KeyRound, Edit3, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function DashboardSettings() {
  const { user, organization, updateProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
  });

  const handleSave = async () => {
    setSaving(true);
    const result = await updateProfile(form);
    setSaving(false);
    if (result.success) {
      toast.success("Profile updated successfully");
    } else {
      toast.error(result.error || "Failed to update profile");
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-settings">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Settings</h1>
        <p className="text-[#94A3B8] mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <User className="w-5 h-5 text-[#7C3AED]" />
          Profile Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#E2E8F0] mb-2">First Name</label>
            <Input
              value={form.first_name}
              onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-first-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Last Name</label>
            <Input
              value={form.last_name}
              onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-last-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Email</label>
            <ChangeEmailInline currentEmail={user?.email || ""} />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                data-testid="settings-phone"
              />
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#7C3AED] hover:bg-[#8B5CF6]"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </motion.div>

      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#10B981]" />
          Account Details
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Role</p>
            <p className="text-white font-medium capitalize">{user?.role || "Member"}</p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Member Since</p>
            <p className="text-white font-medium">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Status</p>
            <p className="text-[#10B981] font-medium">Active</p>
          </div>
        </div>
      </motion.div>

      {/* Organization */}
      {organization && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#F59E0B]" />
            Organization
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Name</p>
              <p className="text-white font-medium">{organization.name}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Plan</p>
              <p className="text-[#F59E0B] font-medium capitalize">{organization.plan || "Free"}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Change Password */}
      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) {
      toast.error("Fill in both current and new password.");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Password updated. Use the new password next time you log in.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-[#0F172A] border border-white/10 rounded-xl p-6"
      data-testid="change-password-card"
    >
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-[#F59E0B]" />
        Change Password
      </h2>
      <div className="grid gap-4 max-w-xl">
        <PasswordField
          label="Current Password"
          value={form.current_password}
          onChange={(v) => update("current_password", v)}
          show={show.current}
          toggle={() => setShow((p) => ({ ...p, current: !p.current }))}
          testid="current-password"
        />
        <PasswordField
          label="New Password"
          value={form.new_password}
          onChange={(v) => update("new_password", v)}
          show={show.next}
          toggle={() => setShow((p) => ({ ...p, next: !p.next }))}
          testid="new-password"
          help="At least 8 characters."
        />
        <PasswordField
          label="Confirm New Password"
          value={form.confirm_password}
          onChange={(v) => update("confirm_password", v)}
          show={show.confirm}
          toggle={() => setShow((p) => ({ ...p, confirm: !p.confirm }))}
          testid="confirm-password"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="change-password-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lock className="w-4 h-4 mr-1.5" />}
            {saving ? "Updating…" : "Update Password"}
          </Button>
        </div>
      </div>
    </motion.form>
  );
}

function ChangeEmailInline({ currentEmail }) {
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const open = () => {
    setNewEmail(currentEmail);
    setPassword("");
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!newEmail || !password) {
      toast.error("Enter the new email and your current password.");
      return;
    }
    if (newEmail.trim().toLowerCase() === (currentEmail || "").toLowerCase()) {
      toast.error("New email is the same as your current email.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(
        `${API}/auth/change-email`,
        { current_password: password, new_email: newEmail.trim().toLowerCase() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.token) {
        localStorage.setItem("auth_token", res.data.token);
      }
      toast.success("Email updated. Use the new email next time you sign in.");
      setEditing(false);
      setPassword("");
      // Reload so AuthContext picks up the new user + token everywhere.
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change email.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input
            value={currentEmail}
            disabled
            className="pl-10 bg-white/5 border-white/10 text-[#94A3B8] cursor-not-allowed"
            data-testid="settings-current-email"
          />
        </div>
        <Button
          type="button"
          onClick={open}
          variant="outline"
          className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
          data-testid="settings-change-email-btn"
        >
          <Edit3 className="w-4 h-4 mr-1.5" />
          Change
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="settings-change-email-form">
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
        <Input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="new@email.com"
          className="pl-10 bg-[#020817] border-white/10 text-white"
          data-testid="settings-new-email"
          autoFocus
        />
      </div>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Confirm your current password"
          className="pl-10 bg-[#020817] border-white/10 text-white"
          autoComplete="current-password"
          data-testid="settings-change-email-password"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-white"
          data-testid="settings-change-email-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {saving ? "Updating…" : "Save New Email"}
        </Button>
        <Button
          type="button"
          onClick={cancel}
          variant="outline"
          className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
          data-testid="settings-change-email-cancel"
        >
          <XIcon className="w-4 h-4 mr-1.5" />
          Cancel
        </Button>
      </div>
      <p className="text-xs text-[#64748B]">
        For security, please confirm your current password. You&apos;ll keep using this account — only your sign-in email changes.
      </p>
    </form>
  );
}

function PasswordField({ label, value, onChange, show, toggle, testid, help }) {
  return (
    <div>
      <label className="block text-sm text-[#94A3B8] mb-1.5">{label}</label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-[#020817] border-white/10 text-white pr-10"
          autoComplete="new-password"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white p-1"
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {help && <p className="text-xs text-[#64748B] mt-1">{help}</p>}
    </div>
  );
}
